import { useEffect, useRef, useState } from "react";
import {
  EmailTemplateCreateRequest,
  EmailTemplateResponse,
  EmailTemplateUpdateRequest,
} from "api/main/data-contracts";
import { createApi } from "util/ApiCreator";
import { EmailTemplateAdminController } from "api/main/EmailTemplateAdminController";
import { adminTheme } from "theme/adminTheme";
import { findEmailTemplateType } from "./EmailTemplateTypeTranslation";
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  LinearProgress,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RefreshIcon from "@mui/icons-material/Refresh";
import * as yup from "yup";
import { useFormik } from "formik";

const validationSchema = yup.object({
  type: yup.string().required("Šablono pavadinimas yra privalomas."),
  subject: yup.string().required("Tema yra privaloma."),
  body: yup.string().required("Turinys yra privalomas."),
});

export default function EmailTemplatePage() {
  const api = useRef(createApi("emailtemplate") as EmailTemplateAdminController);

  const [emailTemplates, setEmailTemplates] = useState<EmailTemplateResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateResponse | null>(null);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    handleDataFetch();
  }, []);

  async function handleDataFetch() {
    setLoading(true);
    const data = await api.current.findAll();
    setEmailTemplates(data || []);
    setLoading(false);
  }

  const formik = useFormik({
    initialValues: {
      id: undefined as number | undefined,
      type: "",
      subject: "",
      body: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (values.id) {
        await api.current.update(values.id, values as EmailTemplateUpdateRequest);
      } else {
        await api.current.create(values as EmailTemplateCreateRequest);
      }
      await handleDataFetch();
      setDialogOpen(false);
      resetForm();
      setSelectedTemplate(null);
    },
  });

  const handleOpenCreate = () => {
    formik.resetForm();
    formik.setValues({ id: undefined, type: "", subject: "", body: "" });
    setSelectedTemplate(null);
    setPreviewMode(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (template: EmailTemplateResponse) => {
    formik.setValues({
      id: template.id,
      type: template.type || "",
      subject: template.subject || "",
      body: template.body || "",
    });
    setSelectedTemplate(template);
    setPreviewMode(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    formik.resetForm();
    setSelectedTemplate(null);
    setPreviewMode(false);
  };

  const handleDelete = async () => {
    if (selectedTemplate?.id) {
      await api.current.delete(selectedTemplate.id);
      await handleDataFetch();
    }
    setDeleteDialogOpen(false);
    setSelectedTemplate(null);
  };

  const handleOpenDelete = (template: EmailTemplateResponse) => {
    setSelectedTemplate(template);
    setDeleteDialogOpen(true);
  };

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100%" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: adminTheme.colors.textPrimary,
              }}
            >
              Laiškų šablonai
            </Typography>
            <Typography
              variant="body"
              sx={{ color: adminTheme.colors.textSecondary }}
            >
              Valdykite el. laiškų šablonus
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Tooltip title="Atnaujinti">
              <IconButton
                onClick={handleDataFetch}
                sx={{
                  bgcolor: adminTheme.colors.background,
                  border: `1px solid ${adminTheme.colors.border}`,
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{
                background: adminTheme.gradients.primary,
                "&:hover": { background: adminTheme.gradients.primaryHover },
                px: 3,
                py: 1.5,
              }}
            >
              Naujas šablonas
            </Button>
          </Box>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Stats Cards */}
        <Box sx={{ display: "flex", gap: 3, mb: 4, flexWrap: "wrap" }}>
          <Card
            sx={{
              flex: 1,
              minWidth: 200,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Iš viso šablonų
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary }}
            >
              {emailTemplates.length}
            </Typography>
          </Card>
        </Box>

        {/* Table */}
        <Card
          sx={{
            borderRadius: adminTheme.borderRadius.lg,
            border: `1px solid ${adminTheme.colors.border}`,
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: adminTheme.colors.backgroundAlt,
                    "& th": {
                      fontWeight: 600,
                      color: adminTheme.colors.textPrimary,
                      borderBottom: `2px solid ${adminTheme.colors.border}`,
                      py: 2,
                    },
                  }}
                >
                  <TableCell width="5%"></TableCell>
                  <TableCell>Tipas</TableCell>
                  <TableCell>Tema</TableCell>
                  <TableCell align="right">Veiksmai</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emailTemplates.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: adminTheme.colors.textMuted,
                        }}
                      >
                        <EmailIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                        <Typography>Nėra laiškų šablonų</Typography>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={handleOpenCreate}
                          sx={{ mt: 2 }}
                        >
                          Sukurti pirmą šabloną
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  emailTemplates.map((template) => (
                    <>
                      <TableRow
                        key={template.id}
                        sx={{
                          transition: adminTheme.transitions.fast,
                          "&:hover": {
                            bgcolor: "rgba(99, 102, 241, 0.04)",
                          },
                        }}
                      >
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setExpandedRow(
                                expandedRow === template.id ? null : template.id ?? null
                              )
                            }
                          >
                            {expandedRow === template.id ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={findEmailTemplateType(template.type)?.label || template.type}
                            size="small"
                            sx={{
                              bgcolor: "rgba(99, 102, 241, 0.1)",
                              color: adminTheme.colors.primary,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                bgcolor: "rgba(99, 102, 241, 0.1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: adminTheme.colors.primary,
                              }}
                            >
                              <EmailIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Typography sx={{ fontWeight: 500 }}>
                              {template.subject}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Redaguoti">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(template)}
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ištrinti">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDelete(template)}
                              sx={{ color: adminTheme.colors.error }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{ py: 0, borderBottom: expandedRow === template.id ? undefined : "none" }}
                        >
                          <Collapse in={expandedRow === template.id} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 3, bgcolor: adminTheme.colors.backgroundAlt }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                Turinio peržiūra:
                              </Typography>
                              <Box
                                sx={{
                                  p: 2,
                                  bgcolor: adminTheme.colors.background,
                                  borderRadius: adminTheme.borderRadius.sm,
                                  border: `1px solid ${adminTheme.colors.border}`,
                                }}
                                dangerouslySetInnerHTML={{ __html: template.body || "" }}
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: adminTheme.borderRadius.lg,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 600,
              borderBottom: `1px solid ${adminTheme.colors.border}`,
            }}
          >
            {selectedTemplate ? `"${selectedTemplate.subject}" redagavimas` : "Naujo laiško šablono sukūrimas"}
          </DialogTitle>
          <form onSubmit={formik.handleSubmit}>
            <DialogContent sx={{ pt: 3 }}>
              <TextField
                fullWidth
                label="Šablono pavadinimas (tipas)"
                name="type"
                value={formik.values.type}
                onChange={formik.handleChange}
                error={formik.touched.type && Boolean(formik.errors.type)}
                helperText={formik.touched.type && formik.errors.type}
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                label="Tema"
                name="subject"
                value={formik.values.subject}
                onChange={formik.handleChange}
                error={formik.touched.subject && Boolean(formik.errors.subject)}
                helperText={formik.touched.subject && formik.errors.subject}
                sx={{ mb: 3 }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <Button size="small" onClick={() => setPreviewMode(!previewMode)}>
                  {previewMode ? "Kodas" : "Peržiūra"}
                </Button>
              </Box>
              {previewMode ? (
                <Box
                  sx={{
                    border: `1px solid ${adminTheme.colors.border}`,
                    borderRadius: adminTheme.borderRadius.sm,
                    p: 2,
                    minHeight: 200,
                  }}
                  dangerouslySetInnerHTML={{ __html: formik.values.body }}
                />
              ) : (
                <TextField
                  fullWidth
                  label="Turinys (HTML)"
                  name="body"
                  value={formik.values.body}
                  onChange={formik.handleChange}
                  error={formik.touched.body && Boolean(formik.errors.body)}
                  helperText={formik.touched.body && formik.errors.body}
                  multiline
                  rows={10}
                />
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: `1px solid ${adminTheme.colors.border}` }}>
              <Button onClick={handleCloseDialog}>Atšaukti</Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  background: adminTheme.gradients.primary,
                  "&:hover": { background: adminTheme.gradients.primaryHover },
                }}
              >
                {selectedTemplate ? "Išsaugoti" : "Sukurti šabloną"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: adminTheme.borderRadius.lg,
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Laiškų šablono ištrynimas</DialogTitle>
          <DialogContent>
            <Typography>
              Ar tikrai norite ištrinti laiškų šabloną temai "{selectedTemplate?.subject}"?
            </Typography>
            <Typography sx={{ mt: 2, color: adminTheme.colors.error }}>
              Šis veiksmas yra negrįžtamas.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeleteDialogOpen(false)}>Atšaukti</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
            >
              Ištrinti
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
